import { useState } from "react";
import { AlertTriangle, ArrowRight, BarChart3, CalendarDays, CheckCircle2, ChevronRight, Inbox, LayoutDashboard, MailCheck, RefreshCw, Sparkles, Star } from "lucide-react";
import { api } from "../services/api";
import { compactNumber, formatDateTime, safeText } from "../lib/formatters";
import { Stars, StatusBadge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { ErrorState, LoadingState } from "../components/ui/Feedback";
import { useDashboardReport } from "../hooks/useDashboardReport";
import { PeriodControls, PeriodDetails, PeriodSummary } from "../features/dashboard/PeriodReport";

const DATES = [["today", "今天"], ["yesterday", "昨天"], ["this_week", "本周"], ["last_week", "上周"], ["last_7_days", "近 7 天"]];
const number = (value) => value == null ? "—" : Number(value).toLocaleString("zh-CN");
function openInquiry(item) {
  const id = Number(item?.id);
  if (!Number.isFinite(id) || id <= 0) return;
  sessionStorage.setItem("bluesun:selected-inquiry", String(id));
  window.location.hash = `#/inquiries/${id}`;
}

function CustomerRow({ item, high = false }) {
  const name = safeText(item.customer_name || item.sender_name, "未知客户");
  return <button className="workspace-customer" onClick={() => openInquiry(item)}>
    <span className="avatar">{name.slice(0, 1)}</span>
    <span className="workspace-customer__copy">
      <span><strong title={name}>{name}</strong><small>{safeText(item.country, "国家待确认")}</small></span>
      <small title={safeText(item.product || item.subject)}>{safeText(item.product || item.subject, "需求待确认")}</small>
    </span>
    <span className="workspace-customer__state">
      {high ? <Stars value={item.business_value_rating} /> : <StatusBadge status={item.status} label={item.status_label} />}
      <time>{formatDateTime(item.received_at)}</time>
    </span>
    <ChevronRight size={16} />
  </button>;
}

function Kpi({ label, value, caption, icon: Icon, tone, onClick }) {
  const Tag = onClick ? "button" : "article";
  return <Tag className={"workspace-kpi workspace-kpi--" + tone} onClick={onClick}>
    <div className="workspace-kpi__top"><span>{label}</span><Icon size={18} /></div>
    <strong>{number(value)}</strong>
    <small>{caption}{onClick && <ArrowRight size={13} />}</small>
  </Tag>;
}

function Notice({ message, onRetry }) {
  if (!message) return null;
  return <div className="workspace-notice" role="alert"><AlertTriangle size={16} /><span>{message}</span><Button size="sm" onClick={onRetry}>重试</Button></div>;
}

function CompactTrend({ report, showReport }) {
  const daily = report.data?.daily || [];
  const max = Math.max(1, ...daily.flatMap((row) => [row.received, row.sent]));
  const x = (index) => 35 + index * 265 / Math.max(1, daily.length - 1);
  const y = (value) => 120 - value / max * 90;
  return <aside className="surface workspace-overview">
    <header><div><span className="workspace-section-label">BUSINESS PULSE</span><h2>近 7 日业务趋势</h2></div><BarChart3 size={19} /></header>
    <div className="workspace-legend"><span><i />收到询盘</span><span><i />实际已回复</span></div>
    <Notice message={report.error ? "趋势更新失败，保留上次数据。" : ""} onRetry={report.reload} />
    {!report.data ? (report.error ? <p className="workspace-muted">暂无可用趋势数据</p> : <LoadingState compact label="正在加载趋势…" />) : <>
      <svg className="workspace-chart" viewBox="0 0 330 150" role="img" aria-label="近7日收件与回复趋势；具体数值见下方表格">
        {[0, 0.5, 1].map((factor) => <g key={factor}><line x1="35" x2="300" y1={y(max * factor)} y2={y(max * factor)} /><text x="27" y={y(max * factor) + 4} textAnchor="end">{Number((max * factor).toFixed(1))}</text></g>)}
        {["received", "sent"].map((field) => <g key={field} className={"workspace-chart__series workspace-chart__series--" + field}>
          {field === "received" && <polygon points={"35,120 " + daily.map((row, i) => x(i) + "," + y(row[field])).join(" ") + " 300,120"} />}
          <polyline points={daily.map((row, i) => x(i) + "," + y(row[field])).join(" ")} />
          {daily.map((row, i) => <circle key={row.date} cx={x(i)} cy={y(row[field])} r="3"><title>{row.date + " · " + (field === "received" ? "收到 " : "回复 ") + row[field]}</title></circle>)}
        </g>)}
        {daily.filter((_, index) => index % 3 === 0).map((row) => <text key={row.date} x={x(daily.indexOf(row))} y="142" textAnchor="middle">{row.date.slice(5)}</text>)}
      </svg>
      <div className="workspace-trend-table" role="region" aria-label="近7日趋势数值" tabIndex={0}>
        <table><thead><tr><th scope="col">日期</th><th scope="col">收件</th><th scope="col">回复</th></tr></thead><tbody>
          {[...daily].reverse().map((row) => <tr key={row.date}><th scope="row">{row.date.slice(5)}</th><td>{number(row.received)}</td><td>{number(row.sent)}</td></tr>)}
        </tbody></table>
      </div>
    </>}
    <button className="workspace-report-link" onClick={showReport}>查看详细统计与 AI 用量<ArrowRight size={15} /></button>
  </aside>;
}

function Workbench({ data, loading, error, reload, report, trend, queueView, setQueueView, showReport }) {
  const metrics = data?.metrics || {};
  const ai = data?.ai_usage || {};
  const services = data?.services || {};
  const high = data?.priority_high_value || [];
  const failed = data?.priority_failed || [];
  const rows = queueView === "high" ? high : failed;
  const queueError = error;
  const queueLoading = loading;
  const hasQueue = Boolean(data);
  const retryQueue = reload;
  const summary = report.data?.summary;
  const period = report.data?.period;
  return <>
    <div className="workspace-context"><span><CalendarDays size={14} />{period ? period.start_date + (period.start_date === period.end_date ? "" : " — " + period.end_date) : "正在读取日期…"}<small>日期统计 · 北京时间</small></span><span role="status"><i />{report.loading || loading ? "正在更新…" : "页面可见时每 30 秒更新"}</span></div>
    <Notice message={report.error ? "日期统计更新失败" + (report.data ? "，显示上次数据。" : "。") : ""} onRetry={report.reload} />
    <Notice message={error ? "当前待办与服务配置更新失败" + (data ? "，显示上次数据。" : "。") : ""} onRetry={reload} />
    <section className="workspace-kpis" aria-label="核心业务指标">
      <Kpi label="收到询盘" value={summary?.received} caption="所选日期 · 按收件时间" icon={Inbox} tone="blue" />
      <Kpi label="实际已回复" value={summary?.sent} caption="所选日期 · 按发送时间" icon={MailCheck} tone="green" />
      <Kpi label="处理失败" value={metrics.failed} caption="全部日期 · 点击排查" icon={AlertTriangle} tone="red" onClick={() => setQueueView("failed")} />
      <Kpi label="高价值询盘" value={summary?.high_value} caption={"所选日期 · ≥ " + (report.data?.high_value_min_rating || data?.high_value_min_rating || 4) + " 星"} icon={Star} tone="purple" onClick={() => setQueueView("high")} />
    </section>
    <div className="workspace-body">
      <section className="surface workspace-queue">
        <header><div><span className="workspace-section-label">ACTION CENTER</span><h2>需要关注的客户</h2></div><button className="workspace-text-link" onClick={() => { window.location.hash = "#/inquiries"; }}>询盘工作台<ArrowRight size={15} /></button></header>
        <div className="workspace-queue-tabs" role="group" aria-label="待办客户分组">
          {[["high", "高价值优先", data ? high.length : undefined], ["failed", "处理失败", metrics.failed]].map(([key, label, count]) => <button key={key} aria-pressed={queueView === key} className={queueView === key ? "is-active" : ""} onClick={() => setQueueView(key)}>{label}<span>{number(count)}</span></button>)}
        </div>
        <p className="workspace-queue-note">全部日期未完成事项，不受上方日期筛选影响。</p>
        {queueError && hasQueue && <Notice message="客户列表更新失败，保留上次数据。" onRetry={retryQueue} />}
        <div key={queueView} className="workspace-customer-list" tabIndex={0} role="region" aria-label="待办客户列表，可独立滚动">
          {!hasQueue ? (queueError ? <ErrorState message={queueError} onRetry={retryQueue} /> : <LoadingState compact label="正在加载重点客户…" />) : rows.length ? rows.map((item) => <CustomerRow key={item.id} item={item} high={queueView === "high"} />) : <div className="workspace-empty"><CheckCircle2 size={30} /><strong>{queueView === "failed" ? "没有处理失败的询盘" : "暂无待处理的高价值客户"}</strong><span>新事项会自动更新到这里</span></div>}
        </div>
        <footer><span>{hasQueue ? (queueView === "high" ? "优先展示最高等级的 " + rows.length + " 位客户" : "展示 " + rows.length + " / " + number(metrics.failed) + " 位 · 更多请进入询盘工作台") : "正在读取当前账号待办"}</span><span>{queueLoading ? "更新中…" : "点击客户查看详情"}<ChevronRight size={12} /></span></footer>
      </section>
      <CompactTrend report={trend} showReport={showReport} />
    </div>
    <footer className="surface workspace-footer">
      <div className="workspace-services" title="以下表示账号配置已填写，不代表已实时探测连接健康或余额。"><span className="workspace-footer-title">服务配置</span>
        {[["IMAP", services.imap], ["SMTP", services.smtp], ["AI", services.ai], ["联网背调", services.search]].map(([label, ready]) => <span key={label}><i className={data && ready ? "is-ready" : ""} />{label}<small>{!data ? "待读取" : ready ? "已配置" : "待配置"}</small></span>)}
      </div>
      <div className="workspace-ai"><Sparkles size={17} /><span>今日 Token <strong>{data ? compactNumber(ai.today_tokens) : "—"}</strong></span><span>本月成功率 <strong>{data && ai.call_count ? Number(ai.success_rate || 0).toFixed(1) + "%" : "—"}</strong></span></div>
    </footer>
  </>;
}

export default function DashboardPage({ data, loading, error, reload, notify, user, active = true }) {
  const [view, setView] = useState("workspace");
  const [queueView, setQueueView] = useState("high");
  const [syncing, setSyncing] = useState(false);
  const report = useDashboardReport(active, user?.id);
  const trend = useDashboardReport(active && view === "workspace" && report.selection.period !== "last_7_days", user?.id, "last_7_days");
  const switchView = (next) => {
    setView(next);
    document.querySelector(".dashboard-page")?.closest(".route-panel")?.scrollTo({ top: 0 });
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const result = await api.sync();
      notify(result.message || "邮箱同步任务已启动。", "success");
      reload(); report.reload(); trend.reload();
    } catch (err) { notify("同步失败", "danger", err.message); }
    finally { setSyncing(false); }
  };
  return <div className={"page dashboard-page dashboard-page--" + view}>
    <header className="workspace-header">
      <div className="workspace-title"><span><LayoutDashboard size={23} /></span><div><h1>{view === "workspace" ? "业务工作台" : "统计报表"}</h1><p>{view === "workspace" ? "掌握今日进展，聚焦每一个待办" : "按日期复盘业务表现与 AI 使用情况"}</p></div></div>
      <div className="workspace-toolbar">
        {view === "workspace" && <label className="workspace-date"><CalendarDays size={16} /><select aria-label="工作台统计日期" value={report.selection.period} onChange={(e) => {
          if (e.target.value === "custom") switchView("report");
          else report.select({ period: e.target.value });
        }}>{DATES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}<option value="custom">自定义日期…</option></select></label>}
        <nav className="workspace-view-switch" aria-label="首页视图"><button aria-current={view === "workspace" ? "page" : undefined} onClick={() => switchView("workspace")}><LayoutDashboard size={15} />工作台</button><button aria-current={view === "report" ? "page" : undefined} onClick={() => switchView("report")}><BarChart3 size={15} />统计报表</button></nav>
        <Button variant="primary" icon={RefreshCw} loading={syncing} onClick={sync}>同步邮箱</Button>
      </div>
    </header>
    {view === "workspace" ? <Workbench data={data} loading={loading} error={error} reload={reload} report={report} trend={report.selection.period === "last_7_days" ? report : trend} queueView={queueView} setQueueView={setQueueView} showReport={() => { report.select({ period: "last_7_days" }); switchView("report"); }} /> : <div className="dashboard-report-view">
      <PeriodControls report={report} /><PeriodSummary report={report} /><PeriodDetails report={report} />
      {report.data && <section className="surface dashboard-period-inquiries">
        <div className="section-header"><div><h2>所选日期的最近询盘</h2><p>共 {number(report.data.summary.received)} 封，展示最近 10 封；点击查看邮件、背调和回复。</p></div></div>
        {report.data.recent.length ? report.data.recent.map((item) => <CustomerRow key={item.id} item={item} />) : <div className="empty-inline">所选日期没有收到询盘。</div>}
      </section>}
      <section className="surface workspace-account-usage">
        <h2>当前账号 AI 使用概况</h2><p>今天及本月的账号汇总，不受上方报表日期影响。</p>
        <Notice message={error ? "账号用量更新失败，保留上次数据。" : ""} onRetry={reload} />
        <dl>
          <div><dt>今日 Token</dt><dd>{data ? compactNumber(data.ai_usage?.today_tokens) : "—"}</dd></div>
          <div><dt>本月 Token</dt><dd>{data ? compactNumber(data.ai_usage?.month_tokens) : "—"}</dd></div>
          <div><dt>本月调用成功率</dt><dd>{data?.ai_usage?.call_count ? Number(data.ai_usage.success_rate || 0).toFixed(1) + "%" : "—"}</dd></div>
          <div><dt>本月成功调用平均耗时</dt><dd>{data?.ai_usage?.call_count ? Number(data.ai_usage.average_duration_seconds || 0).toFixed(1) + " 秒" : "—"}</dd></div>
        </dl>
      </section>
    </div>}
  </div>;
}
